// Because of inconsistent '%25' decoding behavior from the 'history' library,
// this function might be necessary to properly encode '%' as '%25' in a URI before decoding.

export function encodeURIPercentChar(str: string): string {
  return str.replace(/%(?![0-9A-F]{2})/g, '%25');
}

export const decodeURIComponentSafe = makeSafeURIFunction(decodeURIComponent);
type URIFunction = typeof encodeURI;

export function makeSafeURIFunction(fn: URIFunction): URIFunction {
  return (str: string) => {
    try {
      return fn(str);
    } catch {
      return str;
    }
  };
}

// The 'history' library specifically decodes '%25' into '%', while it leaves other
// special characters in their encoded forms. When we try to decode URIs on top of history's
// weird decoding, we run into errors because of the inconsistent special character decoding
// done by history.
// This is a dirty workaround that catches malformed URIs and manually encodes '%' into '%25',
// so that we can decode appropriately.
export function decodeURIComponentHistoryHax(str: string): string {
  try {
    return decodeURIComponent(str);
  } catch {
    const encodedStr = encodeURIPercentChar(str);
    return decodeURIComponentSafe(encodedStr);
  }
}

// The 'history' library runs decodeURI() over a location's pathname while
// react-router builds a location, so a pathname carrying an escape that
// decodeURI rejects throws a URIError from inside a render — which unmounts the
// app rather than landing on a not-found page. Two shapes reach us that way: a
// bare '%' (a literal percent in a run group or report name) and a multi-byte
// escape cut short ('...-%E2'), which is what a link truncated mid-string looks
// like. Escaping every '%' keeps the rest of the path intact and leaves the bad
// escape to render literally, so the link resolves — or 404s — instead of
// crashing the page. The path is returned unchanged when it already decodes.
export function makeRouterSafePath(path: string): string {
  const endOfPathname = path.search(/[?#]/);
  const pathname = endOfPathname === -1 ? path : path.slice(0, endOfPathname);
  try {
    decodeURI(pathname);
    return path;
  } catch {
    return pathname.replace(/%/g, '%25') + path.slice(pathname.length);
  }
}

export function parseRunTabPath(pathStr?: string) {
  return pathStr == null || pathStr === ''
    ? []
    : pathStr.split('/').map(p => decodeURIComponentHistoryHax(p));
}

export function removeUrlProtocolPrefix(s: string) {
  return s.replace(/(^\w+:|^)\/\//, '');
}

/**
 * makeDownloadUrl makes the provided URL trigger a browser download by adding
 * the 'content-disposition=attachment' URL parameter. This is helpful when
 * downloading files from object storage, where an anchor tag's 'download'
 * attribute is ignored due to the request being cross-origin.
 */
export function makeDownloadUrl(baseUrl: string, filename?: string): string {
  const url = new URL(baseUrl);

  url.searchParams.set(
    'Content-Disposition',
    !filename ? 'attachment' : `attachment; filename="${filename}"`
  );

  return url.toString();
}
